import { reactive, inject, onMounted, onUnmounted, computed, watch } from 'vue';
import { type tabs } from '@shared/components/List/ListTypes';
import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
import { ScopeAccess } from '@shared/directives/Scope.access';
import { injectorAccessInjectionKey } from '@cwd/lib/provider-keys';
import { ContextAccess } from '@shared/Root/Context.access';
import { setupTags } from '@shared/components/Tags/Tags';
import { combineUrlSegments } from '@shared/utils/combineUrlSegments';
import { Permissions } from '@shared/Root/Permissions';
import { debounce } from '@shared/utils';
import { type dateFilter } from '@shared/components/DateRangePicker';
import { getWriteAccessDisabledIds } from '@cwd/Acl/useAclAccess';
// import { issuesSignalRService, type IssueUpdate } from '@shared/services/signalR/issues-signalr-service';
import {
    getIssues,
    getCountPerIssueStatus,
    getCategories,
    deleteIssues,
    pauseIssue,
    moveIssueToDrafts,
    moveIssueToSent,
    pausingIssue,
    getSentIssueStatistics,
} from './Issues.access';
import {
    type Issue,
    type status,
    statusName,
    type statusObj,
    type CountPerIssueStatus,
    OrderByValue,
    type Category,
    IssueStatus,
    IssueSourceFilter,
    type GetIssuesFilter,
    type IssueStatistics,
} from './interfaces';
import { OrderByDirection } from '@shared/components/List/ListTypes';

export const timeout = 5000;

export const orderOptions = (currentTab?: statusName) => [
    { display: $Resources.IssuesSortByLastModifiedLabel, key: OrderByValue.LastModified },
    { display: $Resources.CreatedLabel, key: OrderByValue.Created },
    ...(currentTab === statusName.sent ? [{ display: $Resources.IssuesSortByLastStartDateSentLabel, key: OrderByValue.StartedDateTime }] : []),
    { display: $Resources.IssuesSortBySubjectLabel, key: OrderByValue.Subject },
    { display: $Resources.IssuesSortByNameLabel, key: OrderByValue.IssueName },
    { display: $Resources.IssuesSortByCategoryLabel, key: OrderByValue.CategoryName },
];

export function setupIssues() {
    const injectorAccess = inject(injectorAccessInjectionKey)!;
    const contextAccess: ContextAccess = injectorAccess.injector.get(ContextAccess);
    const scopeAccess: ScopeAccess = injectorAccess.injector.get(ScopeAccess);
    const tags = setupTags();

    const hasTemplatesReadAccess = scopeAccess.hasScope({
        Feature: 'IssueTemplates',
        Permission: Permissions.Read,
    });

    const pageSize = 15;

    const sourceFilterKey = 'issueSourceFilter' + contextAccess.state.currentAccount?.AccountId;
    const storedSourceFilter = localStorage.getItem(sourceFilterKey);

    const state = reactive<{
        issues: Issue[];
        loading: boolean;
        allLoaded: boolean;
        page: number;
        currentFilter: string;
        selected: string[];
        orderByValue: OrderByValue;
        fromAddress: string;
        orderByDirection: OrderByDirection;
        currentTabData: null | statusObj;
        tabs: tabs;
        isCreateNew: boolean;
        countPerIssueStatus: CountPerIssueStatus | null;
        abortController: AbortController | null;
        categories: Category[] | null;
        loadingCategories: boolean;
        selectedCategoryIds: string[];
        previewUrl: string | null;
        storedIssueStatistics: {
            [key: string]: IssueStatistics;
        };
        dateFilter: dateFilter | null;
        sourceFilter: IssueSourceFilter;
    }>({
        issues: [],
        loading: false,
        allLoaded: false,
        page: 0,
        currentFilter: '',
        selected: [],
        orderByValue: OrderByValue.LastModified,
        fromAddress: '',
        orderByDirection: 'desc',
        currentTabData: tabData()[statusName.draft],
        tabs: tabData(),
        isCreateNew: false,
        countPerIssueStatus: null,
        abortController: null,
        categories: null,
        loadingCategories: false,
        selectedCategoryIds: [],
        previewUrl: null,
        storedIssueStatistics: {},
        dateFilter: { start: null, end: null },
        sourceFilter: storedSourceFilter !== null ? parseInt(storedSourceFilter) : IssueSourceFilter.NoConnection,
    });

    // Define the issue status change listener function
    /*
    const handleIssueChange = (updatedIssue: IssueUpdate) => {
        console.log('Issue changed:', updatedIssue);

        const foundIssue = state.issues.find((item) => item.IssueId === updatedIssue.IssueId);

        if (foundIssue) {
            foundIssue.Subject = updatedIssue.Subject;
            // foundIssue.Status = updatedIssue.Status;
            // foundIssue.SentCount = updatedIssue.SentCount;
            // foundIssue.FailedCount = updatedIssue.FailedCount;
            // foundIssue.RecipientCount = updatedIssue.RecipientCount;
            // foundIssue.ClickCount = updatedIssue.ClickCount;
            // foundIssue.OpenCount = updatedIssue.OpenCount;

            // Object.assign(found, updatedIssue);
        }
    };
    */

    onMounted(async () => {
        const location = window.location;
        const url = new URL(location.toString());
        const params = url.searchParams;
        const tab = (params.get('tab') as statusName) || undefined;

        // Select the initial tab before awaiting the counts. Awaiting first would let a tab
        // the user clicks while the counts request is in flight be reset back to the default,
        // and it delays loading the issues until the counts have come back.
        switchTab(tab);

        const countPerIssueStatus = await getCountPerIssueStatus();
        state.countPerIssueStatus = countPerIssueStatus;

        // temp fix for exclude scheduled from recurring
        state.countPerIssueStatus.RecurringIssuesCount = state.countPerIssueStatus.RecurringIssuesCount - state.countPerIssueStatus.ScheduledIssuesCount;

        // Update tab visibility as soon as counts are available (don't rely on switchTab only)
        updateTabsToShow();

        if (hasTemplatesReadAccess) {
            state.tabs.template.hidden = false;
        }

        // Initialize SignalR connection for issue updates
        // await issuesSignalRService.connect();

        // Add listener for issue status changes
        // issuesSignalRService.addListener(handleIssueChange);
    });

    onUnmounted(() => {
        // Clean up SignalR listeners when component is unmounted
        // issuesSignalRService.removeListener(handleIssueChange);
    });

    watch(
        () => tags.state.selected,
        () => {
            resetPage();
            handleLoadIssues(getTagIds());
        },
        { deep: true }
    );

    const updateTabsToShow = () => {
        if (!state.countPerIssueStatus) return;

        if (state.countPerIssueStatus.ScheduledIssuesCount > 0) {
            state.tabs.scheduled.hidden = false;
        }

        if (state.countPerIssueStatus.RecurringIssuesCount > 0) {
            state.tabs.recurring.hidden = false;
        }

        if (state.countPerIssueStatus.PausedIssuesCount > 0) {
            state.tabs.paused.hidden = false;
        }

        if (state.countPerIssueStatus.SendingIssuesCount > 0) {
            state.tabs.sending.hidden = false;
        }

        if (state.countPerIssueStatus.FailedIssuesCount > 0) {
            state.tabs.failed.hidden = false;
        }
    };

    const switchTab = (newStatusName: string) => {
        const statusNameValue = (newStatusName || statusName.draft) as statusName;
        state.loading = true;

        resetPage();
        updateTabsToShow();
        toggleDateFilter(statusNameValue);

        if (statusNameValue === statusName.sent) {
            state.orderByValue = OrderByValue.StartedDateTime;
            state.orderByDirection = 'desc';
        } else {
            state.orderByValue = OrderByValue.LastModified;
            state.orderByDirection = 'desc';
        }

        state.currentTabData = tabData()[statusNameValue];

        handleLoadIssues();
    };

    const toggleDateFilter = (statusNameValue: statusName) => {
        if (statusNameValue === statusName.sent) {
            state.dateFilter = { start: null, end: null };
        } else {
            state.dateFilter = null;
        }
    };

    const resetPage = () => {
        clearSelection();
        state.page = 0;
        state.issues = [];
    };

    const loadMore = () => {
        state.page += 1;
        handleLoadIssues();
    };

    const clearSelection = () => {
        state.selected = [];
    };

    const getTagIds = () => {
        return tags.state.selected?.map((s) => s.TagId) || [];
    };

    const handleLoadCategories = async () => {
        if (state.categories !== null) return;
        if (state.loadingCategories) return;
        state.loadingCategories = true;
        state.categories = await getCategories();
        state.loadingCategories = false;
    };

    const handleLoadIssues = async (tagIds: string[] = getTagIds()) => {
        if (!state.currentTabData) return;

        state.loading = true;
        state.allLoaded = false;

        // Cancel any existing request
        if (state.abortController) {
            state.abortController.abort();
        }

        // Create new AbortController for this request
        state.abortController = new AbortController();

        const filter: GetIssuesFilter = {
            status: state.currentTabData!.name,
            page: state.page,
            pageSize: pageSize,
            currentFilter: state.currentFilter,
            fromAddress: state.fromAddress,
            sortOrder: state.orderByValue,
            orderByDirection: state.orderByDirection,
            tagIds: tagIds,
            categoryIds: state.selectedCategoryIds,
            start: null,
            end: null,
            sourceFilter: state.sourceFilter,
        };

        if (state.currentTabData?.name === statusName.sent) {
            filter.start = state.dateFilter?.start || null;
            filter.end = state.dateFilter?.end || null;
        }

        let aborted = false;

        getIssues(filter, { signal: state.abortController.signal })
            .then((result: Issue[]) => {
                if (result && result.length > 0) {
                    state.issues = state.page === 0 ? result : [...state.issues, ...result];
                } else {
                    state.allLoaded = true;
                }
            })
            .catch((error) => {
                if (error.name === 'AbortError') {
                    aborted = true;
                    return;
                }

                bsAlert({ type: AlertTypes.Danger, content: 'Unable to load items.' });
            })
            .finally(() => {
                if (!aborted) {
                    state.loading = false;
                    state.abortController = null;
                }
            });
    };

    const filterOptions = () => {
        return ['Subject', 'IssueName', 'Category.Name'];
    };


    const getTotalCount = computed(() => {
        switch (state.currentTabData?.name) {
            case statusName.draft:
                return state.countPerIssueStatus?.DraftIssuesCount;
            case statusName.issueVariation:
                return state.countPerIssueStatus?.IssueVariationCount;
            case statusName.scheduled:
                return state.countPerIssueStatus?.ScheduledIssuesCount;
            case statusName.recurring:
                return state.countPerIssueStatus?.RecurringIssuesCount;
            case statusName.paused:
                return state.countPerIssueStatus?.PausedIssuesCount;
            case statusName.sending:
                return state.countPerIssueStatus?.SendingIssuesCount;
            case statusName.sent:
                return state.countPerIssueStatus?.SentIssuesCount;
            case statusName.template:
                return state.countPerIssueStatus?.TemplateIssuesCount;
            case statusName.failed:
                return state.countPerIssueStatus?.FailedIssuesCount;
            default:
                return 0;
        }
    });

    const filterUpdated = (newVal: string) => {
        debounce(async () => {
            resetPage();
            state.currentFilter = newVal;
            await handleLoadIssues();
        });
    };

    const fromAddressUpdated = (newVal: string) => {
        // Set right away: it's the input's modelValue, and FormInput resets the input to a stale modelValue when it re-renders
        state.fromAddress = newVal;
        debounce(async () => {
            resetPage();
            await handleLoadIssues();
        });
    };

    const dateRangeChanged = async (start: string, end: string) => {
        resetPage();
        state.dateFilter!.start = start && start !== 'Invalid Date' && start !== '' ? start + ' 00:00' : null;
        state.dateFilter!.end = end && end !== 'Invalid Date' && end !== '' ? end + ' 23:59' : null;
        await handleLoadIssues();
    };

    const handleUpdateOrder = async (orderByValue: string, orderByDirection: OrderByDirection) => {
        state.orderByValue = orderByValue as OrderByValue;
        state.orderByDirection = orderByDirection;
        await handleLoadIssues();
    };

    const categoryUpdated = async (newVal: string[]) => {
        resetPage();
        state.selectedCategoryIds = newVal;
        await handleLoadIssues();
    };

    const toggleSourceFilter = (flag: IssueSourceFilter) => {
        resetPage();
        if (flag === IssueSourceFilter.None) {
            state.sourceFilter = IssueSourceFilter.None;
        } else {
            state.sourceFilter ^= flag;
        }
        localStorage.setItem(sourceFilterKey, String(state.sourceFilter));
        handleLoadIssues();
    };

    const showPreview = (issueId: string) => {
        state.previewUrl = combineUrlSegments(contextAccess.state.currentAccount!.PublicEventUrl.PublicHost, 'Issues', '' + issueId, '?preview=true');
    };

    const disabledCheckboxes = computed(() => {
        const eventDisabledIds = state.issues
            .filter((issue) => issue.Event && issue.Event.ParticipantStatusChangeIssueId === issue.IssueId)
            .map((issue) => issue.IssueId);

        const aclDisabledIds = getWriteAccessDisabledIds(
            state.issues,
            (issue) => issue.IssueId,
            (issue) => issue.CurrentUserAccessLevel
        );

        return [...new Set([...eventDisabledIds, ...aclDisabledIds])];
    });

    const handleDisableCheckboxes = computed(() => {
        return (
            state.currentTabData?.name !== statusName.draft &&
            state.currentTabData?.name !== statusName.sent &&
            state.currentTabData?.name !== statusName.template
        );
    });

    const handleDelete = () => {
        if (state.loading) return;
        state.loading = true;
        const list = state.selected.map((id) => ({ IssueId: id }));

        deleteIssues(list)
            .then(() => {
                bsAlert({ title: $Resources.AlertIssuesDeletedHeading, content: $Resources.AlertIssuesDeletedBody });

                state.selected.forEach((id) => {
                    const found = state.issues.find((item) => item.IssueId === id);
                    if (found) found.Deleted = true;
                });

                addToCountForCurrentStatus(-state.selected.length);
                clearSelection();
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorDeletingIssuesHeading, content: $Resources.ErrorDeletingIssuesBody });
            })
            .finally(() => {
                state.loading = false;
            });
    };

    const addToCountForCurrentStatus = (number: number) => {
        switch (state.currentTabData?.name) {
            case statusName.draft:
                state.countPerIssueStatus!.DraftIssuesCount += number;
                break;
            case statusName.issueVariation:
                state.countPerIssueStatus!.IssueVariationCount += number;
                break;
            case statusName.scheduled:
                state.countPerIssueStatus!.ScheduledIssuesCount += number;
                break;
            case statusName.recurring:
                state.countPerIssueStatus!.RecurringIssuesCount += number;
                break;
            case statusName.paused:
                state.countPerIssueStatus!.PausedIssuesCount += number;
                break;
            case statusName.sending:
                state.countPerIssueStatus!.SendingIssuesCount += number;
                break;
            case statusName.sent:
                state.countPerIssueStatus!.SentIssuesCount += number;
                break;
            case statusName.template:
                state.countPerIssueStatus!.TemplateIssuesCount += number;
                break;
        }
    };

    const handlePauseIssue = (issueId: string) => {
        if (state.loading) return;
        state.loading = true;

        pauseIssue(issueId)
            .then(() => {
                bsAlert({ title: $Resources.AlertIssueAbortedHeader, content: $Resources.AlertIssueAbortedBody });
                addToCountForCurrentStatus(-1);
                state.tabs.paused.hidden = false;
                switchTab(statusName.paused);
                addToCountForCurrentStatus(1);
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorAbortingIssueHeader, content: $Resources.ErrorAbortingIssueBody });
            })
            .finally(() => {
                state.loading = false;
            });
    };

    const handlePausingIssue = (issueId: string) => {
        if (state.loading) return;
        state.loading = true;

        pausingIssue(issueId)
            .then(() => {
                bsAlert({ title: $Resources.AlertIssueAbortedHeader, content: $Resources.AlertIssueAbortedBody });

                const callback = () => {
                    state.countPerIssueStatus!.SendingIssuesCount--;
                    state.countPerIssueStatus!.PausedIssuesCount++;
                };

                checkIfPaused(issueId, callback);
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorAbortingIssueHeader, content: $Resources.ErrorAbortingIssueBody });
            })
            .finally(() => {
                state.loading = false;
            });
    };

    const checkIfPaused = async (issueId: string, callback: () => void) => {
        setTimeout(async () => {
            const data = await getSentIssueStatistics(issueId);
            if (data.Status !== IssueStatus.Pausing) {
                callback();
            } else {
                checkIfPaused(issueId, callback);
            }
        }, timeout);
    };

    const handleMoveIssueToDrafts = (issueId: string) => {
        if (state.loading) return;
        state.loading = true;

        moveIssueToDrafts(issueId)
            .then(() => {
                bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AertIssueMovedToDrafts });
                addToCountForCurrentStatus(-1);
                switchTab(statusName.draft);
                addToCountForCurrentStatus(1);
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeader, content: $Resources.ErrorMovingIssueToDrafts });
            })
            .finally(() => {
                state.loading = false;
            });
    };

    const handleMoveIssueToSent = (issueId: string) => {
        if (state.loading) return;
        state.loading = true;

        moveIssueToSent(issueId)
            .then(() => {
                bsAlert({ title: $Resources.AlertSuccessHeading, content: $Resources.AlertIssueMovedToSent });
                addToCountForCurrentStatus(-1);
                switchTab(statusName.draft);
                addToCountForCurrentStatus(1);
            })
            .catch(() => {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorHeader, content: $Resources.ErrorMovingIssueToSent });
            })
            .finally(() => {
                state.loading = false;
            });
    };

    const itemsNotDeleted = computed(() => state.issues.filter((item) => !item.Deleted));

    const handleIssueStatisticsLoaded = (issue: IssueStatistics) => {
        state.storedIssueStatistics![issue.IssueId] = issue;
    };

    const updateSendingCount = (currentTab: statusName) => {
        if (currentTab === statusName.scheduled) {
            state.countPerIssueStatus!.ScheduledIssuesCount--;
        } else if (currentTab === statusName.recurring) {
            state.countPerIssueStatus!.RecurringIssuesCount--;
        } else if (currentTab === statusName.issueVariation) {
            state.countPerIssueStatus!.IssueVariationCount--;
        }

        state.countPerIssueStatus!.SendingIssuesCount++;
    };

    const updateSentCount = () => {
        state.countPerIssueStatus!.SendingIssuesCount--;
        state.countPerIssueStatus!.SentIssuesCount++;
    };

    const updateCheckedItems = (newVal: (string | number)[]) => {
        state.selected = newVal as string[];
    };

    return {
        state,
        switchTab,
        updateCheckedItems,
        updateSendingCount,
        updateSentCount,
        handleDelete,
        handlePauseIssue,
        handlePausingIssue,
        handleMoveIssueToDrafts,
        handleMoveIssueToSent,
        handleIssueStatisticsLoaded,
        handleLoadCategories,
        dateRangeChanged,
        handleUpdateOrder,
        categoryUpdated,
        toggleSourceFilter,
        showPreview,
        fromAddressUpdated,
        filterUpdated,
        orderOptions,
        filterOptions,
        loadMore,
        clearSelection,
        itemsNotDeleted,
        disabledCheckboxes,
        handleDisableCheckboxes,
        getTotalCount,
        tags,
    };
}

export const tabData = (): status => {
    return {
        [statusName.all]: { display: $Resources.ToggleOptionsAll, description: '', name: statusName.all, hidden: true },
        [statusName.draft]: { display: $Resources.DraftLabel, description: $Resources.IssuesDraftsIntroduction, name: statusName.draft },
        [statusName.issueVariation]: {
            display: $Resources.IssueVariationTab,
            description: $Resources.IssueVariationIntroduction,
            name: statusName.issueVariation,
            hidden: true,
        },
        [statusName.scheduled]: {
            display: $Resources.ScheduledLabel,
            description: $Resources.IssuesScheduledIntroduction,
            name: statusName.scheduled,
            hidden: true,
        },
        [statusName.recurring]: {
            display: $Resources.IssuesRecurringHeading,
            description: $Resources.IssuesRecurringIntroduction,
            name: statusName.recurring,
            hidden: true,
        },
        [statusName.paused]: {
            display: $Resources.IssuesPausedTab,
            description: $Resources.IssuesAbortedIntroduction,
            name: statusName.paused,
            hidden: true,
        },
        [statusName.sending]: {
            display: $Resources.IssuesSendingHeading,
            description: $Resources.IssuesSendingIntroduction,
            name: statusName.sending,
            hidden: true,
        },
        [statusName.sent]: { display: $Resources.SentLabel, description: $Resources.IssuesSentIntroduction, name: statusName.sent },
        [statusName.failed]: {
            display: $Resources.IssuesFailedTab,
            description: $Resources.IssuesFailedIntroduction,
            name: statusName.failed,
            hidden: true,
        },
        [statusName.template]: {
            display: $Resources.IssuesTemplatesTab,
            description: $Resources.IssueTemplatesIntroduction,
            name: statusName.template,
            hidden: true,
        },
    };
};
